import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../utils/api';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import MultiDayEventBadge from '../../components/MultiDayEventBadge';  // Iter-408
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Plus, Calendar, MapPin, Users, Rocket, List, CalendarDays, Clock, Loader2, ChevronLeft, ChevronRight, User, Phone, Mail, ExternalLink, Filter, Search, XCircle, AlertTriangle, CheckSquare } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { formatTime12h, formatTimeRange, formatDateShort, formatDateFull, parseLocalDate, toLocalYMD, enumerateDates } from '../../utils/timeFormat';
import { groupShiftsByEvent } from '../../hooks/useGroupedEvents';
import GroupedEventCard from '../../components/GroupedEventCard';
import BulkActionBar from '../../components/client/BulkActionBar';
import { toast } from 'sonner';

const ClientShifts = () => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDetail, setShiftDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Iter-211 — Active vs Past tabs. Active = end_time within 6h or future,
  // not cancelled. Past = older than 6h or cancelled.
  const [lifecycleTab, setLifecycleTab] = useState('active');
  
  // Cancellation state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancellationCheck, setCancellationCheck] = useState(null);

  // Iter-306 — Bulk selection state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleId = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const isSelected = (id) => selectedIds.includes(id);
  const exitBulkMode = () => { setBulkMode(false); setSelectedIds([]); };

  useEffect(() => {
    fetchShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifecycleTab]);

  // Re-fetch when tab regains focus so booking changes from another
  // tab (admin assignment, talent cancellation) reflect without a hard reload.
  useEffect(() => {
    const onFocus = () => fetchShifts();
    const onVis = () => { if (!document.hidden) fetchShifts(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const fetchShifts = async () => {
    try {
      // Iter-211 — past_only flag drives the backend split. Default = active.
      const params = lifecycleTab === 'past' ? { past_only: true } : {};
      const response = await api.get('/client/shifts', { params });
      setShifts(response.data);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch shift detail with assigned talent
  const fetchShiftDetail = async (shiftId) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/client/shifts/${shiftId}`);
      // Backend now returns an authoritative `bookings` array (joined to talent_profiles
      // directly) — use it so direct-hire / admin-assigned / backup-converted bookings
      // show up. The old code derived bookings from applications only and missed those.
      const apiBookings = Array.isArray(res.data.bookings) ? res.data.bookings : [];
      // Show ALL active bookings (confirmed + checked_in + completed). Previously
      // this filtered to status='confirmed' only, so once talent checked in or the
      // shift completed, the roster vanished and the modal showed 0/N — even though
      // the list row correctly counted them. Aligns with backend bookings_count.
      const ACTIVE_STATUSES = new Set(['confirmed', 'checked_in', 'completed']);
      const bookings = apiBookings
        .filter(b => ACTIVE_STATUSES.has((b.status || '').toLowerCase()))
        .map(b => ({
          id: b.id,
          talent_name: b.talent_name,
          talent_photo: b.talent_photo,
          talent_city: b.talent_city,
          talent_state: b.talent_state,
          status: b.status,
          talent_confirmed: b.talent_confirmed,
          confirmed_at: b.confirmed_at,
          // Iter-213+ — surface hours worked / approval state on the modal so
          // past/completed events show what was actually delivered (not just who
          // was booked). Previously past events had identical UX to active ones,
          // so clients had no quick way to see hours / approval status.
          hours_worked: b.hours_worked,
          hours_approved: b.hours_approved,
          // Backup/standby metadata — rendered as chips in the modal so the
          // client can see at a glance whether a slot was filled by a primary
          // or an auto-activated backup, and why.
          booking_type: b.booking_type,
          is_standby: b.is_standby,
          standby_position: b.standby_position,
          standby_activated: b.standby_activated,
          standby_activated_at: b.standby_activated_at,
          standby_activation_reason: b.standby_activation_reason,
          backup_activated: b.backup_activated,
          backup_activated_at: b.backup_activated_at,
        }));

      setShiftDetail({
        ...res.data.shift,
        bookings,
        invoice: res.data.invoice || null,
        // `pending_applications` is now authoritative on the shift object.
        pending_applications: res.data.shift.pending_applications ?? 0,
      });
    } catch (err) {
      toast.error('Failed to load shift details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Handle shift click
  const handleShiftClick = (shift) => {
    setSelectedShift(shift);
    fetchShiftDetail(shift.id);
  };

  // Check if shift can be cancelled
  const checkCancellation = async (shiftId) => {
    try {
      const res = await api.get(`/client/shifts/${shiftId}/cancellation-check`);
      setCancellationCheck(res.data);
      return res.data;
    } catch (err) {
      toast.error('Failed to check cancellation eligibility');
      return null;
    }
  };

  // Handle opening cancel dialog
  const handleCancelClick = async () => {
    if (!shiftDetail?.id) return;
    const check = await checkCancellation(shiftDetail.id);
    if (check) {
      setShowCancelDialog(true);
    }
  };

  // Handle actual cancellation
  const handleConfirmCancel = async () => {
    if (!shiftDetail?.id) return;
    setCancelling(true);
    try {
      const res = await api.delete(`/client/shifts/${shiftDetail.id}/cancel?reason=${encodeURIComponent(cancelReason)}`);
      toast.success(res.data.message || 'Shift cancelled successfully');
      
      // Close dialogs and refresh
      setShowCancelDialog(false);
      setSelectedShift(null);
      setShiftDetail(null);
      setCancelReason('');
      setCancellationCheck(null);
      fetchShifts(); // Refresh the list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel shift');
    } finally {
      setCancelling(false);
    }
  };

  // Filter shifts
  const filteredShifts = shifts.filter(s => {
    // Status filter
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        s.venue_name?.toLowerCase().includes(search) ||
        s.role_name?.toLowerCase().includes(search) ||
        s.city?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-amber-100 text-amber-800 border border-amber-300',
      open: 'bg-green-100 text-green-800',
      filled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => (
    status === 'draft' ? 'Draft · Pending confirmation' : status
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-shifts-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/client/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button onClick={() => navigate('/client/shifts/create')} className="bg-cyan-500 hover:bg-cyan-600" data-testid="create-shift-button">
            <Plus className="w-4 h-4 mr-2" />
            Create Shift
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <h1 className="text-3xl font-bold">My Shifts</h1>

          {/* Iter-211 — Active / Past tabs (frontend lives here so the
              calendar view still works inside whichever lifecycle the
              user picks). */}
          <Tabs value={lifecycleTab} onValueChange={setLifecycleTab} className="w-full sm:w-auto">
            <TabsList className="grid grid-cols-2 w-full sm:w-[220px]">
              <TabsTrigger value="active" data-testid="lifecycle-active-tab">Active</TabsTrigger>
              <TabsTrigger value="past" data-testid="lifecycle-past-tab">Past</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Filters — mobile-first: search full-width on its own row,
              status + view-toggle wrap below it. ≥sm: inline like before. */}
          <div className="flex flex-wrap items-stretch gap-2 sm:gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none min-w-0 sm:min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search venue, role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-[180px]"
                data-testid="search-input"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] shrink-0" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="filled">Filled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <Tabs value={viewMode} onValueChange={setViewMode} className="bg-white rounded-lg border shrink-0">
              <TabsList className="grid grid-cols-2 h-9">
                <TabsTrigger value="list" className="flex items-center gap-1.5 text-sm px-3" data-testid="list-view-btn">
                  <List className="w-4 h-4" />
                  List
                </TabsTrigger>
                <TabsTrigger value="calendar" className="flex items-center gap-1.5 text-sm px-3" data-testid="calendar-view-btn">
                  <CalendarDays className="w-4 h-4" />
                  Calendar
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Iter-306 — Bulk Select toggle. Only meaningful in list view;
                calendar bulk selection is a different UX (deferred). */}
            {viewMode === 'list' && (
              <Button
                size="sm"
                variant={bulkMode ? 'default' : 'outline'}
                className={
                  bulkMode
                    ? 'bg-cyan-600 hover:bg-cyan-700 text-white h-9 shrink-0'
                    : 'bg-white h-9 shrink-0'
                }
                onClick={() => {
                  if (bulkMode) exitBulkMode();
                  else setBulkMode(true);
                }}
                data-testid="bulk-mode-toggle"
              >
                <CheckSquare className="w-4 h-4 mr-1.5" />
                {bulkMode ? 'Done' : 'Select'}
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : filteredShifts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No shifts yet</p>
              <Button onClick={() => navigate('/client/shifts/create')} className="bg-cyan-500 hover:bg-cyan-600">
                Create Your First Shift
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'calendar' ? (
          /* Enhanced Calendar View - StaffConnect/PopBookings Style */
          <div className="bg-white rounded-2xl border-0 shadow-xl overflow-hidden" data-testid="calendar-view">
            {/* Calendar Header - Premium Gradient */}
            <div className="flex items-center justify-between p-5 bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/10 hover:text-cyan-300 transition-all border border-white/20"
                onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white tracking-tight">
                  {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <p className="text-sm text-cyan-300 font-medium">
                  {shifts.filter(s => {
                    const d = parseLocalDate(s.shift_date);
                    if (!d) return false;
                    return d.getMonth() === selectedMonth.getMonth() && d.getFullYear() === selectedMonth.getFullYear();
                  }).length} shifts scheduled
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/10 hover:text-cyan-300 transition-all border border-white/20"
                onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {/* Calendar Grid */}
            <div className="p-4 bg-gradient-to-b from-gray-50/50 to-white">
              <div className="grid grid-cols-7 gap-2">
                {/* Day headers - Premium style */}
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} className="text-center text-xs font-bold text-slate-500 py-3 tracking-wider">
                    {day}
                  </div>
                ))}
                
                {/* Calendar days */}
                {(() => {
                  const firstDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
                  const lastDay = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
                  const startPadding = firstDay.getDay();
                  const days = [];
                  
                  // Helper to get shift color based on status and tier - Premium gradients
                  const getShiftColor = (shift) => {
                    if (shift.status === 'draft') {
                      return 'bg-gradient-to-r from-amber-400 to-yellow-400 text-white shadow-lg shadow-amber-200 ring-1 ring-amber-300';
                    }
                    if (shift.tier_required === 'premium' || shift.is_featured) {
                      return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-200';
                    }
                    if (shift.status === 'filled') {
                      return 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-green-200';
                    }
                    if (shift.bookings_count > 0 && shift.bookings_count < shift.talent_needed) {
                      return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-200';
                    }
                    if (shift.status === 'open' && (shift.end_date || shift.shift_date) < toLocalYMD(new Date())) {
                      return 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-200';
                    }
                    if (shift.status === 'open') {
                      return 'bg-gradient-to-r from-orange-400 to-amber-400 text-white shadow-lg shadow-orange-200';
                    }
                    return 'bg-gradient-to-r from-gray-400 to-slate-400 text-white shadow-md';
                  };
                  
                  // Format time for display
                  const formatShiftTime = (time) => {
                    if (!time) return '';
                    const [h, m] = time.split(':');
                    const hour = parseInt(h);
                    const ampm = hour >= 12 ? 'p' : 'a';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}${ampm}`;
                  };
                  
                  // Add padding for days before the first of the month
                  for (let i = 0; i < startPadding; i++) {
                    days.push(
                      <div key={`pad-${i}`} className="min-h-[130px] bg-slate-50/30 rounded-xl" />
                    );
                  }
                  
                  // Add each day of the month
                  const todayYmd = toLocalYMD(new Date());
                  for (let day = 1; day <= lastDay.getDate(); day++) {
                    const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
                    const dateStr = toLocalYMD(date);

                    // Filter shifts: include if this date falls within the
                    // shift_date → end_date range (inclusive). Cancelled
                    // shifts still appear so clients can see history but
                    // are dimmed in the cell render below.
                    // Iter-310 — collapse multi-role events to a single
                    // chip per event_group_id on the calendar (spec 1c).
                    const dayShiftsRaw = shifts.filter(s => {
                      const days = enumerateDates(s.shift_date, s.end_date);
                      return days.includes(dateStr);
                    });
                    const _seen = new Set();
                    const dayShifts = dayShiftsRaw.reduce((acc, s) => {
                      if (s.event_group_id) {
                        if (_seen.has(s.event_group_id)) return acc;
                        _seen.add(s.event_group_id);
                        // Build a synthetic "group" chip with the event_title and the role count
                        const siblings = dayShiftsRaw.filter(x => x.event_group_id === s.event_group_id);
                        acc.push({
                          ...s,
                          _isGroup: true,
                          _rolesCount: siblings.length,
                          venue_name: s.event_title || s.venue_name,
                          talent_needed: siblings.reduce((sum, x) => sum + (x.talent_needed || 0), 0),
                          bookings_count: siblings.reduce((sum, x) => sum + (x.bookings_count || 0), 0),
                        });
                      } else {
                        acc.push(s);
                      }
                      return acc;
                    }, []);

                    const isToday = dateStr === todayYmd;
                    const isPast = dateStr < todayYmd;
                    
                    days.push(
                      <div 
                        key={day} 
                        className={`min-h-[130px] rounded-xl transition-all duration-200 ${
                          isToday 
                            ? 'ring-2 ring-cyan-500 ring-offset-2 bg-gradient-to-b from-cyan-50 to-white shadow-lg' 
                            : isPast 
                              ? 'bg-slate-50/50 opacity-60' 
                              : 'bg-white hover:shadow-lg hover:scale-[1.02] border border-slate-100'
                        }`}
                      >
                        {/* Day number header */}
                        <div className={`flex items-center justify-between px-2.5 py-2 ${
                          isToday ? 'bg-cyan-500' : dayShifts.length > 0 ? 'bg-slate-100' : ''
                        } rounded-t-xl`}>
                          <span className={`text-sm font-bold ${
                            isToday ? 'text-white' : isPast ? 'text-slate-400' : 'text-slate-700'
                          }`}>
                            {day}
                          </span>
                          {isToday && (
                            <span className="text-[10px] bg-white text-cyan-600 px-2 py-0.5 rounded-full font-bold shadow-sm">
                              TODAY
                            </span>
                          )}
                          {dayShifts.length > 0 && !isToday && (
                            <span className="text-[10px] bg-cyan-500 text-white px-2 py-0.5 rounded-full font-bold shadow-sm">
                              {dayShifts.length}
                            </span>
                          )}
                        </div>
                        
                        {/* Shifts - Stacked with overflow */}
                        <div className="p-1.5 space-y-1.5 overflow-y-auto max-h-[90px] scrollbar-thin">
                          {dayShifts.slice(0, 3).map(shift => (
                            <div 
                              key={shift.id}
                              className={`text-[11px] px-2.5 py-2 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.03] hover:-translate-y-0.5 ${getShiftColor(shift)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShiftClick(shift);
                              }}
                              data-testid={`shift-card-${shift.id}`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-bold">
                                  {formatShiftTime(shift.start_time)}
                                </span>
                                <div className="flex items-center gap-1">
                                  {shift.duration_days > 1 && (
                                    <span className="text-[9px] bg-white/30 px-1 rounded">{shift.duration_days}d</span>
                                  )}
                                  {shift.is_featured && (
                                    <Rocket className="w-3 h-3 flex-shrink-0 animate-pulse" />
                                  )}
                                </div>
                              </div>
                              <div className="truncate font-semibold">
                                {shift.venue_name || shift.role_name || 'Shift'}
                              </div>
                            </div>
                          ))}
                          {dayShifts.length > 3 && (
                            <button 
                              className="w-full text-[10px] text-center py-1.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 rounded-lg text-slate-600 font-bold transition-all shadow-sm"
                              onClick={() => {
                                toast.info(`${dayShifts.length} shifts on this day. Click to view details.`);
                              }}
                            >
                              +{dayShifts.length - 3} more
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return days;
                })()}
              </div>
            </div>
            
            {/* Premium Legend */}
            <div className="border-t border-slate-200 px-6 py-4 bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-b-2xl">
              <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-100">
                  <span className="w-4 h-4 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 shadow-inner"></span>
                  <span className="text-slate-700 font-semibold">Confirmed</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-100">
                  <span className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 shadow-inner"></span>
                  <span className="text-slate-700 font-semibold">Partially Filled</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-100">
                  <span className="w-4 h-4 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 shadow-inner"></span>
                  <span className="text-slate-700 font-semibold">Open</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-100">
                  <span className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-inner"></span>
                  <span className="text-slate-700 font-semibold">Premium / VIP</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-100">
                  <span className="w-4 h-4 rounded-full bg-gradient-to-r from-red-500 to-rose-500 shadow-inner"></span>
                  <span className="text-slate-700 font-semibold">Urgent</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* List View — Iter-310: multi-role events render as ONE parent
             card with collapsible nested role rows. Single-role events
             render exactly as before. Applicants stay bound to each
             nested shift.id — grouping is display-only. */
          <div className="grid gap-4" data-testid="shifts-list">
            {groupShiftsByEvent(filteredShifts).map((item) => {
              if (!item.isGroup) {
                const shift = item;
                return (
                  <Card
                    key={item.key}
                    className={`hover:shadow-md transition-shadow ${isSelected(shift.id) ? 'ring-2 ring-cyan-400 ring-offset-1' : ''} ${bulkMode ? '' : 'cursor-pointer'}`}
                    onClick={() => {
                      if (bulkMode) { toggleId(shift.id); return; }
                      handleShiftClick(shift);
                    }}
                    data-testid="shift-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start gap-3">
                        {bulkMode && (
                          <div className="pt-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected(shift.id)}
                              onCheckedChange={() => toggleId(shift.id)}
                              data-testid={`shift-select-${shift.id}`}
                              aria-label={`Select ${shift.event_title || shift.venue_name || 'shift'}`}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold">{shift.event_title || shift.venue_name || 'Shift'}</h3>
                            <MultiDayEventBadge
                              dayIndex={shift.day_index}
                              daysInGroup={shift.days_in_group}
                            />
                            <Badge className={getStatusColor(shift.status)} data-testid={`shift-status-${shift.id}`}>{getStatusLabel(shift.status)}</Badge>
                            {shift.tier_required === 'premium' && (
                              <Badge className="bg-purple-100 text-purple-800">Premium</Badge>
                            )}
                            {shift.is_featured && (
                              <Badge className="bg-gradient-to-r from-cyan-500 to-pink-500 text-white">
                                <Rocket className="w-3 h-3 mr-1" />
                                Boosted
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{shift.city}, {shift.state}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDateShort(shift.shift_date)} • {formatTimeRange(shift.start_time, shift.end_time)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span>{shift.bookings_count || 0} / {shift.talent_needed} booked</span>
                              {shift.pending_applications > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/client/shifts/${shift.id}?tab=applicants`);
                                  }}
                                  className="text-cyan-600 font-medium underline-offset-2 hover:underline"
                                  data-testid={`open-applicants-${shift.id}`}
                                >
                                  • {shift.pending_applications} pending applications
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold">${shift.bill_rate}/hr</div>
                          <div className="text-sm text-muted-foreground">Bill Rate</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              // Multi-role grouped event — Iter-310 / Iter-310b
              const groupIds = (item.roles || []).map((r) => r.id).filter(Boolean);
              const groupAllSelected = groupIds.length > 0 && groupIds.every((id) => isSelected(id));
              const toggleGroup = () => {
                setSelectedIds((prev) => {
                  if (groupAllSelected) return prev.filter((id) => !groupIds.includes(id));
                  const merged = new Set([...prev, ...groupIds]);
                  return Array.from(merged);
                });
              };
              return (
                <div key={item.key} className={`relative ${groupAllSelected ? 'ring-2 ring-cyan-400 rounded-xl' : ''}`}>
                  {bulkMode && (
                    <div className="absolute top-3 left-3 z-10 bg-white/95 rounded p-1 shadow-sm" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={groupAllSelected}
                        onCheckedChange={toggleGroup}
                        data-testid={`event-select-${item.event_group_id}`}
                        aria-label={`Select all ${item.roles_count} roles in ${item.event_title || 'this event'}`}
                      />
                    </div>
                  )}
                  <GroupedEventCard
                    group={item}
                    audience="client"
                    defaultExpanded
                    onOpenEvent={(g) => { if (bulkMode) return toggleGroup(); navigate(`/client/job-overview/${g.primary_shift_id}`); }}
                    onOpenRole={(r) => { if (bulkMode) return toggleId(r.id); handleShiftClick(r); }}
                    onOpenApplicants={(r) => navigate(`/client/shifts/${r.id}?tab=applicants`)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter-306 — Sticky bulk-action bar. Renders only when bulk mode is on. */}
      {bulkMode && (
        <BulkActionBar
          selectedIds={selectedIds}
          visibleIds={filteredShifts.map((s) => s.id)}
          allIds={shifts.map((s) => s.id)}
          onSelectAll={() => setSelectedIds(shifts.map((s) => s.id))}
          onSelectVisible={() => setSelectedIds(filteredShifts.map((s) => s.id))}
          onClearSelection={() => setSelectedIds([])}
          onExitBulkMode={exitBulkMode}
          onCompleted={() => {
            setSelectedIds([]);
            fetchShifts();
          }}
        />
      )}
      
      {/* Shift Detail Modal */}
      <Dialog open={!!selectedShift} onOpenChange={() => { setSelectedShift(null); setShiftDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Shift Details</span>
              {selectedShift && (
                <Badge className={getStatusColor(selectedShift.status)}>
                  {getStatusLabel(selectedShift.status)}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Shift details including staffing, assigned talent, and cancellation policy.
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : shiftDetail ? (
            <div className="space-y-6">
              {/* Event Info */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-lg">{shiftDetail.venue_name || shiftDetail.role_name}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{formatDateFull(shiftDetail.shift_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{formatTimeRange(shiftDetail.start_time, shiftDetail.end_time)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{shiftDetail.city}, {shiftDetail.state}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>{shiftDetail.role_name}</span>
                  </div>
                </div>
                {shiftDetail.duration_days > 1 && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Multi-day shift:</strong> {shiftDetail.duration_days} day assignment. Cancellation policy applies per affected shift.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Staffing Status */}
              <div className="bg-white border rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Staffing ({shiftDetail.bookings_count || 0}/{shiftDetail.talent_needed})
                </h4>

                {/* Backup staffing summary — what the client booked */}
                {(shiftDetail.backups_needed || 0) > 0 && (
                  <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="shift-backup-staffing-summary">
                    <div className="font-semibold mb-1">
                      Backup Staffing: {shiftDetail.backups_needed} standby{shiftDetail.backups_needed > 1 ? 's' : ''}
                    </div>
                    <div className="text-amber-800">
                      {shiftDetail.backup_pay_type === 'flat' && shiftDetail.backup_flat_pay != null && (
                        <>Flat standby pay: ${shiftDetail.backup_flat_pay.toFixed(2)} per backup (paid only if activated).</>
                      )}
                      {shiftDetail.backup_pay_type === 'hourly' && shiftDetail.backup_hourly_rate != null && (
                        <>Hourly rate if activated: ${shiftDetail.backup_hourly_rate.toFixed(2)}/hr.</>
                      )}
                      {!shiftDetail.backup_pay_type && 'Auto-activates if a confirmed primary drops.'}
                    </div>
                  </div>
                )}

                {/* Assigned Talent */}
                {shiftDetail.bookings && shiftDetail.bookings.length > 0 ? (
                  <div className="space-y-2">
                    {shiftDetail.bookings.map(booking => {
                      const isBackupSlot = booking.is_standby || booking.booking_type === 'backup' || booking.booking_type === 'standby';
                      const wasActivated = booking.standby_activated || booking.backup_activated;
                      const hasHours = booking.hours_worked != null && Number(booking.hours_worked) > 0;
                      return (
                        <div key={booking.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={booking.talent_photo} />
                              <AvatarFallback>{booking.talent_name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{booking.talent_name}</p>
                                {isBackupSlot && !wasActivated && (
                                  <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800" data-testid={`backup-chip-${booking.id}`}>
                                    Backup{booking.standby_position ? ` #${booking.standby_position}` : ''}
                                  </Badge>
                                )}
                                {wasActivated && (
                                  <Badge className="text-[10px] bg-emerald-600 text-white border-0" data-testid={`backup-activated-chip-${booking.id}`}>
                                    Backup activated
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">{booking.talent_city}, {booking.talent_state}</p>
                              {wasActivated && booking.standby_activation_reason && (
                                <p className="text-[11px] text-emerald-700 mt-0.5">{booking.standby_activation_reason}</p>
                              )}
                              {/* Iter-213+ — Past event line: surface hours worked + approval. */}
                              {hasHours && (
                                <div className="flex items-center gap-2 mt-1" data-testid={`booking-hours-${booking.id}`}>
                                  <span
                                    className="text-[11px] text-slate-700"
                                    data-testid={`roster-hours-${booking.id}`}
                                  >
                                    <Clock className="w-3 h-3 inline mr-0.5 text-slate-400" />
                                    {Number(booking.hours_worked).toFixed(2)} hrs worked
                                  </span>
                                  <Badge
                                    className={booking.hours_approved
                                      ? 'text-[10px] bg-emerald-100 text-emerald-800 border-0'
                                      : 'text-[10px] bg-amber-100 text-amber-800 border-0'}
                                    data-testid={booking.hours_approved
                                      ? `roster-approved-${booking.id}`
                                      : `roster-pending-${booking.id}`}
                                  >
                                    {booking.hours_approved ? 'Approved' : 'Pending approval'}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                          {(() => {
                            const s = (booking.status || '').toLowerCase();
                            // A slot the admin assigned shows status='confirmed' even though
                            // the talent hasn't confirmed yet — surface that honestly.
                            if (s === 'confirmed' && !booking.talent_confirmed) {
                              return (
                                <Badge className="bg-amber-100 text-amber-800 border-0" data-testid={`roster-status-pending-${booking.id}`}>
                                  Pending confirmation
                                </Badge>
                              );
                            }
                            if (s === 'confirmed' && booking.talent_confirmed) {
                              return (
                                <Badge className="bg-emerald-600 text-white border-0" data-testid={`roster-status-confirmed-${booking.id}`}>
                                  Confirmed
                                </Badge>
                              );
                            }
                            return (
                              <Badge variant="secondary" data-testid={`roster-status-${booking.id}`}>
                                {booking.status}
                              </Badge>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No talent assigned yet</p>
                )}

                {shiftDetail.pending_applications > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate(`/client/shifts/${shiftDetail.id}?tab=applicants`)}
                    className="mt-3 w-full p-2 bg-cyan-50 hover:bg-cyan-100 rounded-lg text-center transition-colors"
                    data-testid="open-applicants-from-modal"
                  >
                    <p className="text-sm text-cyan-700 font-medium">
                      {shiftDetail.pending_applications} pending application{shiftDetail.pending_applications > 1 ? 's' : ''} — click to review →
                    </p>
                  </button>
                )}
              </div>

              {/* Cancellation Policy - Client Facing.
                  Iter-213+ — Only relevant for ACTIVE shifts. Hide for
                  completed/cancelled shifts AND for shifts that have already
                  ended (end_date/shift_date in the past). The Past-tab list
                  surfaces shifts via end_time-elapsed, so the modal must use
                  the same predicate or the panels disagree. */}
              {(() => {
                const ymd = (d) => {
                  if (!d) return null;
                  if (typeof d === 'string') return d.slice(0, 10);
                  return d.toISOString().slice(0, 10);
                };
                const todayYmd = new Date().toISOString().slice(0, 10);
                const lastDayYmd = ymd(shiftDetail.end_date || shiftDetail.shift_date);
                const isPast =
                  ['completed', 'cancelled'].includes((shiftDetail.status || '').toLowerCase()) ||
                  (lastDayYmd && lastDayYmd < todayYmd);
                return !isPast;
              })() && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="client-cancellation-policy">
                <h4 className="font-semibold mb-3 text-amber-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Cancellation Policy
                </h4>
                <ul className="space-y-2 text-sm text-amber-800">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><strong>Cancel within 24 hours</strong> → 4-hour minimum payment required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span><strong>Early release</strong> → Paid for hours worked or 4-hour minimum</span>
                  </li>
                  {shiftDetail?.duration_days > 1 && (
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      <span><strong>Multi-day shifts</strong> → Policy applies per affected shift</span>
                    </li>
                  )}
                </ul>
              </div>
              )}

              {/* Iter-213+ — Past event summary panel: replaces the cancellation
                  policy box for completed/cancelled shifts OR shifts whose
                  end-of-event date has already passed. Surfaces a recap link +
                  hours summary so the client gets a real post-event view. */}
              {(() => {
                const ymd = (d) => {
                  if (!d) return null;
                  if (typeof d === 'string') return d.slice(0, 10);
                  return d.toISOString().slice(0, 10);
                };
                const todayYmd = new Date().toISOString().slice(0, 10);
                const lastDayYmd = ymd(shiftDetail.end_date || shiftDetail.shift_date);
                const isPast =
                  ['completed', 'cancelled'].includes((shiftDetail.status || '').toLowerCase()) ||
                  (lastDayYmd && lastDayYmd < todayYmd);
                return isPast;
              })() && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="past-event-summary">
                  <h4 className="font-semibold mb-3 text-slate-800 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Event Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500 text-xs uppercase tracking-wider">Total hours</p>
                      <p className="font-semibold text-slate-900">
                        {(shiftDetail.bookings || [])
                          .reduce((sum, b) => sum + (Number(b.hours_worked) || 0), 0)
                          .toFixed(2)} hrs
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs uppercase tracking-wider">Approved</p>
                      <p className="font-semibold text-slate-900">
                        {(shiftDetail.bookings || []).filter(b => b.hours_approved).length}
                        {' / '}
                        {(shiftDetail.bookings || []).filter(b => Number(b.hours_worked) > 0).length}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/client/recap/${shiftDetail.id}`)}
                      data-testid="open-recap-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Event Recap
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/client/invoices')}
                      data-testid="open-invoice-btn"
                    >
                      View Invoices
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between gap-3 pt-4 border-t">
                {/* Cancel Button - Only show for non-completed/cancelled shifts */}
                {shiftDetail.status !== 'completed' && shiftDetail.status !== 'cancelled' && (
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    onClick={handleCancelClick}
                    data-testid="cancel-shift-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Shift
                  </Button>
                )}
                
                <div className="flex items-center gap-3 ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/client/shifts/${shiftDetail.id}`)}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Full Details
                  </Button>
                </div>
              </div>
            </div>
          ) : selectedShift ? (
            <div className="space-y-4">
              <p className="text-center text-gray-500">Unable to load shift details</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/client/shifts/${selectedShift.id}`)}
              >
                View Full Shift Page
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Cancellation Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Shift
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {cancellationCheck && !cancellationCheck.can_cancel ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 font-medium">{cancellationCheck.reason}</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600">
                      Are you sure you want to cancel this shift? This action cannot be undone.
                    </p>
                    
                    {cancellationCheck && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                        <p className="text-amber-800 font-medium">
                          {cancellationCheck.impact?.talent_to_notify || 0} talent will be notified
                        </p>
                        <p className="text-sm text-amber-700">
                          {cancellationCheck.impact?.billing_note}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Reason for cancellation (optional)
                      </label>
                      <Textarea
                        placeholder="Help us understand why you're cancelling..."
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mt-1"
                        rows={3}
                        data-testid="cancel-reason-input"
                      />
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowCancelDialog(false); setCancelReason(''); setCancellationCheck(null); }}>
              Keep Shift
            </AlertDialogCancel>
            {cancellationCheck?.can_cancel && (
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleConfirmCancel}
                disabled={cancelling}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Shift'
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientShifts;